import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, retry } from 'rxjs/operators';
import { Config } from 'src/environments/config.model';
import { environment } from 'src/environments/environment';
import { OptionType, PAGETYPE } from '../enums/enums';
import { IClientSettings } from '../models/agency-settings.model';
import { QuoteOverrides } from '../models/cancellation-overrides.model';
import { MapItineraryApi } from './mappers/map-itinerary-api.service';
import { MapPaymentApi } from './mappers/map-payment-api.service';
import { URLDataIntegration } from 'src/app/integrations/models/url-data-integration.model';
import { extD } from 'src/app/integrations/models/external-data-integration.model';
import { DashboardComponent } from 'src/app/dashboard/containers/dashboard/dashboard.component';
import { Router } from '@angular/router';
import { DOCSAConfirmation, DOCSARequest } from '../models/docsa.model';
import { ExchangeFareOverrideRequest } from '../models/exchange-override.model';
import { BrandFareRequest, BrandZone } from '../models/fare-family.model';
import { IGdsCredential } from '../models/IGdsCredentials.model';
import { Manual } from '../models/Manual';
import { Operation } from '../models/operation-log.model';
import { PaymentForm } from '../models/reissue.model';
import { Remarks } from '../models/remarks.model';
import { StepResult } from '../models/Step-Result.model';
import { IVoidTransactionQuote, IVoidTransactionResponse } from '../models/void-transaction.model';
import { IRequestAdditionalFlightInfo } from '../models/request-info-flight.model';
import {
  CancellationConfirm,
  CancellationQuote,
  FeedbackModelML,
  PaymentChannel,
  PenaltyFareBasis,
  PenaltyFareBasisMachineLearning,
  Price
} from '../models/cancellation.model';
import {
  IAncillariesWaiveQuote,
  IAncillariesWaiveResponse,
  InvoluntaryExchangeQuote
} from '../models/involuntary-change.model';
import {
  IAdditionalFlightInfo,
  ISRequestValidation,
  ISRequestValidationQuote,
  PnrAvailableOption,
  PnrInfo,
  QuoteRequest,
  SearchForm,
  TicketHistory
} from '../models/itinerary.model';
import {
  Airline,
  AirportParameter,
  ChangePasswordRequest,
  CurrencyParameter,
  Endpoint,
  IAirportByName,
  User
} from '../models/models';
import {
  ICancelSeatRequestService,
  ICancelSeatResponseBody,
  ReserveSeatConfirmation,
  ReserveSeatRequestService,
  SeatMap,
  SeatMapRequest
} from '../models/seat.model';
import {
  AncillariesValidationRequest,
  AncillariesValidationResponse,
  SpecialRequestCategoryQuote,
  SpecialRequestConfirmation,
  SpecialRequestQuoteRequest
} from '../models/special-requests.model';
import {
  ExchangeAvailability,
  ExchangeAvailabilityOption,
  ExchangeFare,
  ExchangeQuoteRequest,
  IssueAncillaries,
  VoluntaryExchangeQuote
} from '../models/voluntary-change.model';
import {
  ISabreAirlineEPRLoginQuote,
  ISabreAirlineEPRLoginResponse
} from '../models/booking-credentials.model';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable()
export class APIService {
  env: Config = environment;
  api = this.env.urlBase;

  constructor(
    private http: HttpClient,
    private mapItineraryApi: MapItineraryApi,
    private mapPaymentApi: MapPaymentApi,
    private router: Router
  ) {}

  // #region Layout services

  postSabreAirlineEPRLogin(
    sabreAirlineEPRLoginQuote: ISabreAirlineEPRLoginQuote
  ): Observable<ISabreAirlineEPRLoginResponse> {
    const urlRequest = `${this.api}${this.env.sabreAirlineEPRLogin}`;
    return this.http.post<ISabreAirlineEPRLoginResponse>(
      urlRequest,
      sabreAirlineEPRLoginQuote,
      httpOptions
    );
  }
  // #endregion

  // #region Itinerary services

  getPnrInfo(
    pnr: string,
    gds: string,
    bookingCode: string,
    prevTransactionId: string | undefined,
    cache: boolean
  ): Observable<PnrInfo> {
    const dataIntegrations = JSON.parse(window.localStorage.getItem(extD)) as URLDataIntegration;
    let urlRequest = `${this.api}${this.env.pnr}/${pnr}/${gds}?bookingCode=${bookingCode}&cache=${cache}&PnrTypeSearch`;

    if (
      dataIntegrations?.transactionId &&
      dataIntegrations?.transactionId != '' &&
      dataIntegrations?.transactionId != null &&
      this.router.url.includes(OptionType.Integrations) &&
      !DashboardComponent.isFirstSearch
    ) {
      urlRequest += `&transactionId=${dataIntegrations?.transactionId}`;
    } else if (prevTransactionId) {
      urlRequest += `&transactionId=${prevTransactionId}`;
    }

    return this.http.get<PnrInfo>(urlRequest).pipe(
      map((response) => {
        return {
          ...response,
          itinerary: this.mapItineraryApi.mapItinerary(response.itinerary)
        };
      })
    );
  }
  // #endregion

  // #region Itinerary Classic history services

  getItineraryClassicHistory(pnr: string, gds: string): Observable<string> {
    const urlRequest = `${this.api}${this.env.getItineraryHistory}/${pnr}/${gds}/logData/pnr/getItineraryClassicHistory/${pnr}/${gds}/${pnr}`;
    return this.http.get(urlRequest, { responseType: 'text' });
  }
  // #endregion

  // #region Ticket history services

  getTicketHistory(body: SearchForm): Observable<TicketHistory[]> {
    const urlRequest = `${this.api}${this.env.pnr}/historical/tickets/logData/pnr/getTicketHistory/${body.pnr}/${body.gds}/${body.bookingCode}`;
    return this.http.post<TicketHistory[]>(urlRequest, body, httpOptions);
  }
  // #endregion

  // #region Ticket services

  getPnrInfoByTKT(body: SearchForm): Observable<PnrInfo> {
    const dataIntegrations = JSON.parse(window.localStorage.getItem(extD)) as URLDataIntegration;
    let urlRequest = `${this.api}${this.env.pnr}/search/tickets/logData/pnr/getPnrInfoByTKT/${body.pnr}/${body.gds}/${body.bookingCode}`;

    if (
      dataIntegrations?.transactionId &&
      dataIntegrations?.transactionId != '' &&
      dataIntegrations?.transactionId != null &&
      this.router.url.includes(OptionType.Integrations) &&
      !DashboardComponent.isFirstSearch
    ) {
      body.transactionId = dataIntegrations?.transactionId;
    }
    return this.http.post<PnrInfo>(urlRequest, body, httpOptions).pipe(
      map((response) => {
        return {
          ...response,
          itinerary: this.mapItineraryApi.mapItinerary(response.itinerary)
        };
      })
    );
  }
  // #endregion

  /**
   *
   */
  getAvailableOption(quote: QuoteRequest): Observable<PnrAvailableOption> {
    if (quote?.itinerary?.segments != undefined) {
      quote.itinerary.segments = this.mapItineraryApi.mapSegments(quote?.itinerary?.segments);
    }
    const urlRequest = `${this.api}${this.env.options}/logData/pnr/getAvailableOption/${quote.pnr}/${quote.gds}/${quote.bookingCode}`;
    return this.http.post<PnrAvailableOption>(urlRequest, quote, httpOptions);
  }

  /**
   * create Remarks to PNR
   */
  createRemarks(remarks: Remarks, pnr: string): Observable<Remarks> {
    const urlRequest = `${this.api}${this.env.remarks}/logData/pnr/createRemarks/${pnr}/${remarks.gds}/${remarks.bookingCode}`;
    return this.http.post<Remarks>(urlRequest, remarks, httpOptions);
  }

  /**
   * Split services
   */
  splitPNR(cancelQuoteRequest: QuoteRequest): Observable<CancellationQuote> {
    const urlRequest = `${this.api}${this.env.splitPnr}/logData/pnr/splitPNR/${cancelQuoteRequest.pnr}/${cancelQuoteRequest.gds}/${cancelQuoteRequest.bookingCode}`;
    return this.http.post<CancellationQuote>(urlRequest, cancelQuoteRequest, httpOptions);
  }

  splitPNRv2(quoteRequest: QuoteRequest): Observable<PnrInfo> {
    const urlRequest = `${this.api}${this.env.splitPnrV2}/logData/pnr/splitPNRv2/${quoteRequest.pnr}/${quoteRequest.gds}/${quoteRequest.bookingCode}`;
    return this.http.post<PnrInfo>(urlRequest, quoteRequest, httpOptions).pipe(
      map((response) => {
        return {
          ...response,
          itinerary: this.mapItineraryApi.mapItinerary(response.itinerary)
        };
      })
    );
  }

  /**
   * Cancellation services
   */
  getCancellationQuote(cancellationQuote: QuoteRequest): Observable<CancellationQuote> {
    const urlRequest = `${this.api}${this.env.cancelRequest}/logData/pnr/getCancellationQuote/${cancellationQuote.pnr}/${cancellationQuote.gds}/${cancellationQuote.bookingCode}`;
    return this.http.post<CancellationQuote>(urlRequest, cancellationQuote, httpOptions);
  }

  /**
   * Penalty Service
   */
  getPenaltiesFareBasis(quote: any): Observable<PenaltyFareBasis[]> {
    const requestPenalty = {
      gds: quote.gds,
      bookingCode: quote.bookingCode,
      pnr: quote.pnr,
      ticketNumbers: quote.passengers.map((passenger) => passenger.ticketNumber),
      pnrOption: quote.pnrOption
    };
    const urlRequest = `${this.api}${this.env.penaltyText}/logData/pnr/getPenaltiesFareBasis/${quote.pnr}/${quote.gds}/${quote.bookingCode}`;
    return this.http.post<PenaltyFareBasis[]>(urlRequest, requestPenalty, httpOptions);
  }

  /**
   *
   * ReadingComprehension ML Model
   *
   */
  getPenaltyTextAI(quote: any, option?: OptionType): Observable<PenaltyFareBasisMachineLearning> {
    let ticketNumbers = [];
    quote?.passengers?.forEach((resp) => {
      ticketNumbers.push(resp?.ticketNumber);
    });
    const requestPenalty = {
      gds: quote?.gds,
      bookingCode: quote?.bookingCode || '',
      pnr: quote?.pnr,
      ticketNumbers,
      pnrOption: option ?? quote?.pnrOption
    };
    const urlRequest = `${this.api}${this.env.penaltyTextAI}/logData/pnr/getPenaltyTextAI/${quote.pnr}/${quote.gds}/${quote.bookingCode}`;
    return this.http.post<PenaltyFareBasisMachineLearning>(urlRequest, requestPenalty, httpOptions);
  }

  sendFeedbackMachineLearning(feedback: FeedbackModelML): Observable<HttpResponse<string>> {
    const urlRequest = `${this.env.urlLogApi}/MachineLearningLog/PutLogMachineLearningPost/${feedback.id}`;
    const feedbackRequest = {
      agentRating: feedback.agentRating,
      agentReview: !feedback.agentRating ? feedback.agentReview : ''
    } as FeedbackModelML;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Api-Key': this.env.logReportApiKey || this.env.apiKey
    });
    return this.http.put<HttpResponse<string>>(urlRequest, feedbackRequest, { headers });
  }

  getPaymentChannel(): Observable<PaymentChannel[]> {
    const urlRequest = `${this.env.urlAdminBase}${this.env.refundProcessingTimes}`;
    return this.http.get<PaymentChannel[]>(urlRequest).pipe(retry(3));
  }

  deletePnr(cancellationQuote: QuoteRequest): Observable<CancellationConfirm> {
    const urlRequest = `${this.api}${this.env.cancelPnr}/logData/pnr/deletePnr/${cancellationQuote.pnr}/${cancellationQuote.gds}/${cancellationQuote.bookingCode}`;
    return this.http.post<CancellationConfirm>(urlRequest, cancellationQuote, httpOptions);
  }

  overrideQuote(quoteOverrides: QuoteOverrides): Observable<CancellationQuote> {
    const urlRequest = `${this.api}${this.env.overrideQuote}/logData/pnr/overrideQuote/${quoteOverrides.pnr}/${quoteOverrides.gds}/${quoteOverrides.bookingCode}`;
    return this.http.post<CancellationQuote>(urlRequest, quoteOverrides, httpOptions);
  }

  /**
   * Special Request Services
   */
  postSpecialRequest(
    specialQuoteRequest: SpecialRequestQuoteRequest
  ): Observable<SpecialRequestConfirmation[]> {
    const urlRequest = `${this.api}${this.env.specialRequest}/logData/pnr/postSpecialRequest/${specialQuoteRequest.pnr}/${specialQuoteRequest.gds}/${specialQuoteRequest.bookingCode}`;
    return this.http.post<SpecialRequestConfirmation[]>(
      urlRequest,
      specialQuoteRequest,
      httpOptions
    );
  }

  getSpecialRequestCategoryQuote(
    specialQuoteRequest: QuoteRequest
  ): Observable<SpecialRequestCategoryQuote> {
    if (specialQuoteRequest?.itinerary?.segments != undefined) {
      specialQuoteRequest.itinerary.segments = this.mapItineraryApi.mapSegments(
        specialQuoteRequest?.itinerary?.segments
      );
    }
    const urlRequest = `${this.api}${this.env.specialRequestQuote}/logData/pnr/getSpecialRequestCategoryQuote/${specialQuoteRequest.pnr}/${specialQuoteRequest.gds}/${specialQuoteRequest.bookingCode}`;
    return this.http.post<SpecialRequestCategoryQuote>(
      urlRequest,
      specialQuoteRequest,
      httpOptions
    );
  }

  /**
   * Voluntary Changes Services
   */
  getVoluntaryQuote(quoteRequest: QuoteRequest): Observable<VoluntaryExchangeQuote> {
    const urlRequest = `${this.api}${this.env.voluntaryQuote}/logData/pnr/getVoluntaryQuote/${quoteRequest.pnr}/${quoteRequest.gds}/${quoteRequest.bookingCode}`;
    return this.http.post<VoluntaryExchangeQuote>(urlRequest, quoteRequest, httpOptions).pipe(
      map((response) => {
        return {
          ...response,
          exchangeSegments: this.mapItineraryApi.mapExchangeQuoteRequest(response.exchangeSegments)
        };
      })
    );
  }

  /**
   * Voluntary Changes Services
   */
  // METODO CREADO PARA FORMATEAR LA FECHA YA QUE CON EL ISOSTRING U OTRO METODO, FORMATEA RESTANDO UN DIA A LA FECHA
  convertDate(departureDateTime, state: boolean) {
    const date = new Date(departureDateTime);
    const day = ('0' + date.getDate()).slice(-2);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const hour = (date.getHours() < 10 ? '0' : '') + date.getHours();
    const minute = (date.getMinutes() < 10 ? '0' : '') + date.getMinutes();

    if (state) {
      return `${date.getFullYear()}-${month}-${day}T${hour}:${minute}:00`;
    } else {
      return `${date.getFullYear()}-${month}-${day}T16:00:00`;
    }
  }

  convertFormBook(form: ExchangeQuoteRequest) {
    form?.selectedOption?.segments.map((segment) => {
      segment.arrivalDate = this.convertDate(segment.arrivalDate, true);
      segment.departureDate = this.convertDate(segment.departureDate, true);
      segment.flights.map((flight) => {
        flight.arrivalDate = this.convertDate(flight.arrivalDate, true);
        flight.departureDate = this.convertDate(flight.departureDate, true);
      });
    });

    return form;
  }

  getAvailabilities(form: ExchangeQuoteRequest): Observable<ExchangeAvailability> {
    const urlRequest = `${this.api}${this.env.voluntaryAvailability}/logData/pnr/getAvailabilities/${form.pnr}/${form.gds}/${form.bookingCode}`;

    form.exchangeSegments.map((flight) => {
      flight.departureDateTime = this.convertDate(flight.departureDateTime, false);
    });

    return this.http.post<ExchangeAvailability>(urlRequest, form, httpOptions).pipe(
      map((response) => {
        return {
          ...response,
          options: response.options.map((option) => this.mapItineraryApi.mapExchangeOption(option))
        };
      })
    );
  }

  exchangeFareOverrideRequest(request: ExchangeFareOverrideRequest): Observable<ExchangeFare> {
    const urlRequest = `${this.api}${this.env.exchangeFareOverride}/logData/pnr/exchangeFareOverrideRequest/${request.pnr}/${request.gds}/${request.bookingCode}`;
    return this.http.post<ExchangeFare>(urlRequest, request, httpOptions);
  }

  voluntaryQuoteRequest(form: ExchangeQuoteRequest): Observable<PnrInfo> {
    const urlRequest = `${this.api}${this.env.voluntaryChangeBook}/logData/reportLogFront/voluntaryQuoteRequest/${form.pnr}/${form.gds}/${form.bookingCode}`;
    form = this.convertFormBook(form);
    return this.http.post<PnrInfo>(urlRequest, form, httpOptions);
  }

  getFareFamilies(form: BrandFareRequest): Observable<BrandZone[]> {
    const urlRequest = `${this.env.urlAdminBase}${this.env.fareFamilies}`;
    return this.http.post<BrandZone[]>(urlRequest, form, httpOptions);
  }

  docsaRequest(request: DOCSARequest): Observable<DOCSAConfirmation> {
    const urlRequest = `${this.api}${this.env.docsaRequest}/logData/pnr/docsaRequest/${request.pnr}/${request.gds}/${request.pnr}`;
    return this.http.post<DOCSAConfirmation>(urlRequest, request, httpOptions);
  }

  /**
   * Involuntary Changes Services
   */
  getInvoluntaryQuote(quoteRequest: QuoteRequest): Observable<InvoluntaryExchangeQuote> {
    if (quoteRequest?.itinerary?.segments != undefined) {
      quoteRequest.itinerary.segments = this.mapItineraryApi.mapSegments(
        quoteRequest?.itinerary?.segments
      );
    }
    const urlRequest = `${this.api}${this.env.involuntaryQuote}/logData/pnr/getInvoluntaryQuote/${quoteRequest.pnr}/${quoteRequest.gds}/${quoteRequest.bookingCode}`;
    return this.http.post<InvoluntaryExchangeQuote>(urlRequest, quoteRequest, httpOptions).pipe(
      map((response) => {
        return {
          ...response,
          exchangeSegments: this.mapItineraryApi.mapExchangeQuoteRequest(response.exchangeSegments),
          airlineOption: this.mapItineraryApi.createExchangeOption(response.newSegments),
          newItinerary: this.mapItineraryApi.mapItinerary(response.newItinerary)
        };
      })
    );
  }

  requestInvoluntaryQuoteBook(exchangeQuoteRequest: ExchangeQuoteRequest): Observable<PnrInfo> {
    if (exchangeQuoteRequest?.selectedOption?.segments != undefined) {
      exchangeQuoteRequest.selectedOption.segments = this.mapItineraryApi.mapSegments(
        exchangeQuoteRequest?.selectedOption?.segments
      );
    }
    const urlRequest = `${this.api}${this.env.involuntaryBook}/logData/reportLogFront/requestInvoluntaryQuoteBook/${exchangeQuoteRequest.pnr}/${exchangeQuoteRequest.gds}/${exchangeQuoteRequest.bookingCode}`;
    return this.http.post<PnrInfo>(urlRequest, exchangeQuoteRequest, httpOptions);
  }

  requestInvoluntaryQuoteFinishBook(
    exchangeQuoteRequest: ExchangeQuoteRequest
  ): Observable<PnrInfo> {
    if (exchangeQuoteRequest?.selectedOption?.segments != undefined) {
      exchangeQuoteRequest.selectedOption.segments = this.mapItineraryApi.mapSegments(
        exchangeQuoteRequest?.selectedOption?.segments
      );
    }
    const urlRequest = `${this.api}${this.env.involuntaryFinishBook}/logData/pnr/requestInvoluntaryQuoteBook/${exchangeQuoteRequest.pnr}/${exchangeQuoteRequest.gds}/${exchangeQuoteRequest.bookingCode}`;
    return this.http.post<PnrInfo>(urlRequest, exchangeQuoteRequest, httpOptions);
  }

  requestExchangeQuoteBook(exchangeQuoteRequest: ExchangeQuoteRequest): Observable<PnrInfo> {
    if (exchangeQuoteRequest?.selectedOption?.segments != undefined) {
      exchangeQuoteRequest.selectedOption.segments = this.mapItineraryApi.mapSegments(
        exchangeQuoteRequest?.selectedOption?.segments
      );
    }
    const urlRequest = `${this.api}${this.env.exchangeBook}/logData/pnr/requestExchangeQuoteBook/${exchangeQuoteRequest.pnr}/${exchangeQuoteRequest.gds}/${exchangeQuoteRequest.bookingCode}`;
    exchangeQuoteRequest = this.convertFormBook(exchangeQuoteRequest);
    return this.http.post<PnrInfo>(urlRequest, exchangeQuoteRequest, httpOptions);
  }

  postInvoluntaryConfirmation(quoteRequest: QuoteRequest): Observable<PnrInfo> {
    if (quoteRequest?.itinerary?.segments != undefined) {
      quoteRequest.itinerary.segments = this.mapItineraryApi.mapSegments(
        quoteRequest?.itinerary?.segments
      );
    }
    const urlRequest = `${this.api}${this.env.involuntaryConfirmation}/logData/pnr/postInvoluntaryConfirmation/${quoteRequest.pnr}/${quoteRequest.gds}/${quoteRequest.bookingCode}`;
    return this.http.post<PnrInfo>(urlRequest, quoteRequest, httpOptions);
  }

  getInvoluntaryAvailabilitiesForOneSegments(
    form: ExchangeQuoteRequest
  ): Observable<ExchangeAvailability> {
    const urlRequest = `${this.api}${this.env.exchangeAvailabilityApi}/logData/pnr/getInvoluntaryAvailabilitiesForOneSegments/${form.pnr}/${form.gds}/${form.bookingCode}`;
    form.exchangeSegments.map((flight) => {
      flight.departureDateTime = this.convertDate(flight.departureDateTime, false);
    });
    return this.http.post<ExchangeAvailability>(urlRequest, form, httpOptions).pipe(
      map((response) => {
        return {
          ...response,
          exchangeSegmentId: form.exchangeSegments[0].id,
          options: response.options.map((option) => {
            return this.mapItineraryApi.mapExchangeOption(option);
          })
        };
      })
    );
  }

  getPricing(exchangeQuoteRequest: ExchangeQuoteRequest): Observable<ExchangeAvailabilityOption> {
    if (exchangeQuoteRequest?.selectedOption?.segments != undefined) {
      exchangeQuoteRequest.selectedOption.segments = this.mapItineraryApi.mapSegments(
        exchangeQuoteRequest?.selectedOption?.segments
      );
    }
    const urlRequest = `${this.api}${this.env.exchangePricing}/logData/pnr/getPricing/${exchangeQuoteRequest.pnr}/${exchangeQuoteRequest.gds}/${exchangeQuoteRequest.bookingCode}`;
    return this.http
      .post<ExchangeAvailabilityOption>(urlRequest, exchangeQuoteRequest, httpOptions)
      .pipe(
        map((response) => {
          return this.mapItineraryApi.mapExchangeOption(response);
        })
      );
  }

  postExchangesBook(quote: QuoteRequest): Observable<PnrInfo> {
    if (quote?.itinerary?.segments != undefined) {
      quote.itinerary.segments = this.mapItineraryApi.mapSegments(quote?.itinerary?.segments);
    }
    const urlRequest = `${this.api}${this.env.exchangePricing}/logData/pnr/postExchangesBook/${quote.pnr}/${quote.gds}/${quote.bookingCode}`;
    return this.http.post<PnrInfo>(urlRequest, quote, httpOptions);
  }

  postAncillariesWaive(quote: IAncillariesWaiveQuote): Observable<IAncillariesWaiveResponse[]> {
    const urlRequest = `${this.api}${this.env.exchangeWaive}/logData/pnr/postAncillariesWaive/${quote?.pnr?.code}/${quote?.pnr?.gds}/${quote?.pnr?.code}`;
    return this.http.post<IAncillariesWaiveResponse[]>(urlRequest, quote, httpOptions);
  }

  // Dashboard services
  getAirports(): Observable<HttpResponse<AirportParameter[]>> {
    return this.http
      .get<AirportParameter[]>(`${this.env.urlAdminBase}${this.env.airports}`, {
        observe: 'response'
      })
      .pipe(retry(3));
  }

  getAirportByName(textMatch: string): Observable<HttpResponse<IAirportByName[]>> {
    return this.http
      .get<IAirportByName[]>(
        `${this.env.urlAdminBase}${this.env.getAirportByName}?name=${textMatch}`,
        {
          observe: 'response'
        }
      )
      .pipe(retry(3));
  }

  getAirlines(): Observable<HttpResponse<Airline[]>> {
    return this.http
      .get<Airline[]>(`${this.env.urlAdminBase}${this.env.airlines}`, { observe: 'response' })
      .pipe(retry(3));
  }

  // Login services
  getUserInfo(token?: string): Observable<HttpResponse<User>> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${token}`
      })
    };

    return this.http.get<User>(`${this.env.urlAdminBase}${this.env.users}`, {
      headers: httpOptions.headers,
      observe: 'response'
    });
  }

  getAgencyLang(agencyId: string): Observable<HttpResponse<string>> {
    return this.http.get<string>(`${this.env.urlAdminBase}${this.env.agencyLang}`, {
      observe: 'response'
    });
  }

  getAuthEndpoints(): Observable<HttpResponse<Endpoint[]>> {
    return this.http.get<Endpoint[]>(`${this.env.urlAdminBase}${this.env.authEndpoints}`, {
      observe: 'response'
    });
  }

  // Operation Log services
  getOperations(pnr: string): Observable<Operation[]> {
    const urlRequest = `${this.env.urlBase}${this.env.operationLog}/${pnr}/logData/pnr/getOperations/${pnr}/NoData/${pnr}`;
    return this.http.get<Operation[]>(urlRequest, httpOptions);
  }

  /**
   * History
   */
  getPNRHistory(
    pnr: string,
    officeId: string,
    country: string,
    gds: string,
    bookingCode: string
  ): Observable<string> {
    const urlRequest = `${this.api}${this.env.getPNRHistory}/${pnr}/${officeId}/${country}/${gds}/logData/pnr/getPNRHistory/${pnr}/${gds}/${bookingCode}`;
    return this.http.get(urlRequest, { responseType: 'text' });
  }

  /**
   * Air Company Notes
   */
  getAirCompanyNotes(
    pnr: string,
    officeId: string,
    country: string,
    gds: string,
    bookingCode: string
  ): Observable<string> {
    const urlRequest = `${this.api}/pnr/GetPnrAirCompanyNotes/${pnr}/${officeId}/${country}/${gds}/${bookingCode}/logData/pnr/getAirCompanyNotes/${pnr}/${gds}/${bookingCode}`;
    return this.http
      .get<{ result: string }>(urlRequest, httpOptions)
      .pipe(map((response) => response.result));
  }

  /**
   * Itinerary History
   */
  getPNRItineraryHistory(
    pnr: string,
    officeId: string,
    country: string,
    gds: string
  ): Observable<StepResult[]> {
    const urlRequest = `${this.api}${this.env.getPNRItineraryHistory}/${pnr}/${officeId}/${country}/${gds}/logData/pnr/createRemarks/${pnr}/${gds}/${pnr}`;
    return this.http.get<StepResult[]>(urlRequest, httpOptions);
  }

  // General
  getCurrencies(): Observable<HttpResponse<CurrencyParameter[]>> {
    return this.http.get<CurrencyParameter[]>(`${this.env.urlAdminBase}${this.env.currencies}`, {
      observe: 'response'
    });
  }

  changePassword(request: ChangePasswordRequest): Observable<any> {
    const urlRequest = `${this.env.urlAuthBase}/users/changePassword`;
    return this.http.post<any>(urlRequest, request, httpOptions);
  }

  openTicket(quote: QuoteRequest, remarks: string[]) {
    quote.remarks = remarks;
    if (quote?.itinerary?.segments != undefined) {
      quote.itinerary.segments = this.mapItineraryApi.mapSegments(quote?.itinerary?.segments);
    }
    const urlRequest = `${this.api}${this.env.openTicket}/logData/pnr/openTicket/${quote.pnr}/${quote.gds}/${quote.bookingCode}`;
    return this.http.post<Price>(urlRequest, quote, httpOptions);
  }

  saveReportLog(log: any) {
    const currentUrl = this.router.url;

    if (
      !(currentUrl.includes(PAGETYPE.pnrHistory) || currentUrl.includes(PAGETYPE.itineraryHistory))
    ) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'X-Api-Key': this.env.logReportApiKey || this.env.apiKey
      });
      return this.http.post(`${this.api}${this.env.operationReportLog}`, log, { headers });
    } else {
      return of(true);
    }
  }

  /**
   * Seat services
   */
  getSeatMap(seatMapRequest: SeatMapRequest): Observable<SeatMap> {
    const urlRequest = `${this.api}${this.env.pnr}${this.env.getSetMap}/logData/pnr/getSeatMap/${this.env.pnr}/${seatMapRequest.gds}/${this.env.pnr}`;
    return this.http.post<SeatMap>(urlRequest, seatMapRequest, httpOptions);
  }

  reserveSeat(
    reserveSeatRequestService: ReserveSeatRequestService
  ): Observable<ReserveSeatConfirmation> {
    const urlRequest =
      `${this.api}${this.env.pnr}${this.env.reserveSeat}?gds=${reserveSeatRequestService.gds}` +
      `&cityOfficeId=${reserveSeatRequestService.cityOfficeId}/logData/pnr/createRemarks/${this.env.pnr}/${reserveSeatRequestService.gds}/${this.env.pnr}`;
    return this.http.post<ReserveSeatConfirmation>(
      urlRequest,
      reserveSeatRequestService.body,
      httpOptions
    );
  }

  cancelSeat(
    gds: string,
    cancelSeatRequestService: ICancelSeatRequestService
  ): Observable<ICancelSeatResponseBody> {
    const urlRequest = `${this.api}${this.env.pnr}${this.env.cancelSeat}?gds=${gds}/logData/pnr/createRemarks/${this.env.pnr}/${gds}/${this.env.pnr}`;
    return this.http.delete<ICancelSeatResponseBody>(urlRequest, {
      body: cancelSeatRequestService
    });
  }

  /**
   * Manual Services
   */

  getManualByLanguage(language: string): Observable<HttpResponse<Manual>> {
    return this.http.get<Manual>(
      `${this.env.urlAdminBase}${this.env.manuals}/UserManualGetByLanguage/${language}`,
      {
        observe: 'response'
      }
    );
  }

  downloadManualById(id: string): Observable<HttpResponse<string>> {
    return this.http.get<string>(
      `${this.env.urlAdminBase}${this.env.manuals}/DownloadUserManual/${id}`,
      {
        responseType: 'text' as 'json',
        observe: 'response'
      }
    );
  }

  // #region Reissue
  payment(
    quote: ExchangeQuoteRequest,
    remarks: string[],
    formOfPayment?: PaymentForm
  ): Observable<HttpResponse<any>> {
    const url = `${this.env.urlBase}${this.env.payment}/logData/pnr/Payment/${quote.pnr}/${quote.gds}/${quote.bookingCode}`;
    const body = this.mapPaymentApi.map(quote, remarks, formOfPayment);
    return this.http.post<any>(url, body, { observe: 'response' });
  }

  reissue(
    quote: ExchangeQuoteRequest,
    remarks: string[],
    formOfPayment: PaymentForm
  ): Observable<PnrInfo> {
    const url = `${this.env.urlBase}${this.env.issue}/logData/pnr/Reissue/${quote.pnr}/${quote.gds}/${quote.bookingCode}`;
    const body = this.mapPaymentApi.map(quote, remarks, formOfPayment);
    return this.http.post<PnrInfo>(url, body, { observe: 'response' }).pipe(
      map((response) => {
        return {
          ...response.body,
          itinerary: this.mapItineraryApi.mapItinerary(response.body.itinerary)
        };
      })
    );
  }
  // #endregion

  // #region Ancillaries Issue
  issueAncillaries(issueAncillaries: IssueAncillaries): Observable<any> {
    const url = `${this.api}${this.env.ancillariesIssue}/logData/pnr/ancillariesIssue/${issueAncillaries.pnrCode}/${issueAncillaries.gds}/${issueAncillaries.pnrCode}`;
    return this.http.post<any>(url, issueAncillaries, { observe: 'response' });
  }
  // #endregion

  // #region Ancillaries Comparison
  loadAncillariesComparison(
    body: AncillariesValidationRequest
  ): Observable<AncillariesValidationResponse> {
    const url = `${this.api}${this.env.ancillariesValidation}/logData/pnr/ancillariesComparison/${body.pnr}/${body.gds}/${body.bookingCode}`;
    return this.http.post<AncillariesValidationResponse>(url, body, httpOptions);
  }
  // #endregion

  /**
   * Agency Settings section
   */
  getAgencySettings(agencyId: string): Observable<HttpResponse<IClientSettings>> {
    return this.http.get<IClientSettings>(
      `${this.env.urlAdminBase}${this.env.agenciesGetSettings}/${agencyId}`,
      {
        observe: 'response'
      }
    );
  }

  getGdsCredentials(token: string): Observable<HttpResponse<IGdsCredential>> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json; charset=UTF-8',
        Authorization: `Bearer ${token}`
      })
    };

    return this.http.get<IGdsCredential>(`${this.env.urlAdminBase}${this.env.GdsCredentials}`, {
      headers: httpOptions.headers,
      observe: 'response'
    });
  }

  postVoidTransaction(
    quote: IVoidTransactionQuote
  ): Observable<HttpResponse<IVoidTransactionResponse[]>> {
    const urlRequest = `${this.api}${this.env.voidTransaction}`;
    return this.http.post<IVoidTransactionResponse[]>(urlRequest, quote, {
      headers: httpOptions.headers,
      observe: 'response'
    });
  }

  postSRequestValidationPerFlight(
    quoteRequest: ISRequestValidationQuote
  ): Observable<HttpResponse<ISRequestValidation[]>> {
    const urlRequest = `${this.api}${this.env.specialRequestFlightValidation}`;
    return this.http.post<ISRequestValidation[]>(urlRequest, quoteRequest, {
      headers: httpOptions.headers,
      observe: 'response'
    });
  }

  getAdditionalFlightInfo(
    quoteRequest: IRequestAdditionalFlightInfo
  ): Observable<HttpResponse<IAdditionalFlightInfo[]>> {
    const urlRequest = `${this.api}${this.env.flightInfo}`;
    return this.http.post<HttpResponse<IAdditionalFlightInfo[]>>(
      urlRequest,
      quoteRequest,
      httpOptions
    );
  }

  getOAGFLTStatus(pnr: string, gds: string): Observable<HttpResponse<string[]>> {
    const urlRequest = `${this.api}${this.env.getFlightInfoStatus}/${pnr}/${gds}`;
    return this.http.get<string[]>(urlRequest, {
      headers: httpOptions.headers,
      observe: 'response'
    });
  }

  // #region Save Log In Log
  postLogInLog(userName: string): Observable<null> {
    const urlRequest = `${this.env.urlAuthBase}${this.env.postUserLogInLog}`;
    return this.http.post<null>(urlRequest, { site: 'opr', userName }, httpOptions);
  }
  // #endregion
}
